/**
 * This service is responsible for preparing the DOM elements required for rendering a Community Site Plan 
 * in the Search Results map. It handles tasks such as creating containers for the Community Site Plan, 
 * transforming its image into base64 format, and preparing tooltips for hover events on the Community Site Plan, 
 * among other functionalities expected from this service.
 */
import {
    buffer,
    catchError,
    debounceTime,
    filter,
    firstValueFrom,
    fromEvent,
    of,
    switchMap,
    take,
    withLatestFrom
} from 'rxjs';
import { store } from '@redux/store';
import getEncodedImage from 'utils/base64Transformations';
import {
    isSitePlanInViewport,
    setZoomLevel,
    toggleDoubleClickZoom,
    updateMapCenter
} from 'libraries/mapLibrary';
import { PaidCommunitySummaryCard } from 'features/summaryCard';
import searchSitePlansInformation$ from '../apis/searchCommunitySitePlans';
import {
    mapStateSlice,
    interactiveSitePlanSlice
} from '../redux/slices';
import {
    mapMarkers,
    selectSitePlans,
    selectZoomLevel
} from '../redux/selectors';
import LotPolygonType from '../enums/lotPolygonType';
import { mapClicked$, mapDragStart$ } from '../redux/observables';
import { zoomLevelLayersLookup, zoomLevelTriggers } from '../constants/sitePlansConstants';
import SitePlanLayer from '../enums/sitePlanLayer';
import getCommunitySummaryCardInformation from '../apis/getCommunitySummaryCard';
import MarkerType from '../enums/markerType';

const actions = {
    ...mapStateSlice.actions,
    ...interactiveSitePlanSlice.actions,
};

const map = document.getElementById('nhs_map');

/**
 * getSitePlan: will retrieve a given community site plan from store.
 * @param {int} sitePlanId identifier of the site plan.
 * @param {int} communityId identifier of the community where site plan belongs.
 * @returns a community site plan.
 */
export const getSitePlan = ({ sitePlanId, communityId }) => {
    const sitePlans = selectSitePlans(store.getState());
    const sitePlanInformation = sitePlanId
        ? sitePlans.find((sitePlan) => sitePlan.sitePlanId === sitePlanId)
        : sitePlans.find((sitePlan) => sitePlan.communityId === communityId)

    const isInInteractiveState =
        selectZoomLevel(store.getState()) >= zoomLevelTriggers.zoomLevelEnterInteractiveStateTrigger;

    return {
        sitePlanInformation,
        isInInteractiveState,
    };
}

/**
 * getDetailUrl returns a string that is the url to the detail page of the community
 * @param {Object} marker with the information to build the detail page url.
 * @returns The string `/communitydetail/community-${this.communityId}` 
 */
const getDetailUrl = (marker) => marker.builderId
        ? `/communitydetail/builder-${marker.builderId}/community-${marker.communityId}`
        : `/communitydetail/community-${marker.communityId}`

/**
 * getActiveMarker function will find a marker in the store by ID and return it.
 * @param {int} mapId is the marker identifier.
 * @returns a marker object from store.
 */
export const getActiveMarker = (mapId) => {
    const state = store.getState();
    const marker = mapMarkers(state).find((element) =>
        element.communityId === mapId &&
        (
            element.type === MarkerType.HousingDevelopment.toString() ||
            element.type === MarkerType.BuildablePlan.toString()
        ));

    return {
        ...marker,
        markerPosition: {
            lat: +marker.latitude,
            lng: +marker.longitude,
        },
        metaData: {
            communityId: marker.communityId,
        },
    };
}

/**
 * setActiveMarker function will activate a marker in the map slice.
 * @param {Object} marker to be markerd as selected.
 */
const setActiveMarker = (marker) => {
   store.dispatch(actions.selectedMarker(marker));
};

/**
 * getInteractiveOverlay function adds an SVG with that outlines the community area. It is useful for mouse over
 * site plan interactions, as it allows us to determine if the mouse is hovering the right place of the SVG in
 * order to show the site plan tooltip to the user.
 * @param {Object} sitePlanConfiguration contains information like rotation degrees for the site plan,
 * its hight and width, the url to fetch with the actual image of the site plan. among others.
 */
export const getInteractiveOverlay = (sitePlanConfiguration) => {
    const rotationCenterX = sitePlanConfiguration.viewboxWidth / 2;
    const rotationCenterY = sitePlanConfiguration.viewboxHeight / 2;
    const svgContent =
        `<svg style="position: absolute; transform: rotate(${sitePlanConfiguration.rotationDegrees}deg);" viewBox="0 0 ${sitePlanConfiguration.viewboxWidth} ${sitePlanConfiguration.viewboxHeight}" baseProfile="basic" width="100%" height="100%" image-rendering="optimizeSpeed" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
            <g transform="rotate(${sitePlanConfiguration.rotationDegrees} ${rotationCenterX} ${rotationCenterY})">    
                ${sitePlanConfiguration.svgMask}
            </g>
        </svg>`;

    const overlayElement = document.createElement("div");
    overlayElement.classList.add('site-plan_overlay');
    overlayElement.id = sitePlanConfiguration.communityId;
    overlayElement.dataset.communityId = sitePlanConfiguration.communityId;
    overlayElement.dataset.sitePlanId = sitePlanConfiguration.sitePlanId;
    overlayElement.dataset.communityMapInteractive = true;
    overlayElement.dataset.url = sitePlanConfiguration.imageUrl;
    overlayElement.dataset.isInteractiveSitePlan = sitePlanConfiguration.isInteractiveSitePlan;
    overlayElement.innerHTML = svgContent;

    return overlayElement;
};

/**
 * 
 * @param {Object} sitePlanConfiguration contains information like rotation degrees for the site plan,
 * its hight and width, the url to fetch with the actual image of the site plan. among others.
 * @param {string} base64image representing the map's url in a format usefull for rendering an img node in map.
 * @returns a string representing the SVG with the content of the site plan.
 */
const getSvgContent = (sitePlanConfiguration, base64image) =>
    `<svg viewBox="0 0 ${sitePlanConfiguration.viewboxWidth} ${sitePlanConfiguration.viewboxHeight}" baseProfile="basic" width="100%" height="100%" image-rendering="optimizeSpeed" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
        ${sitePlanConfiguration.svgMask}
        <g clip-path="url('#${sitePlanConfiguration.communityId}')">
            <image xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="100%" height="100%" xlink:href="${base64image}">
            </image>
        </g>
    </svg>`

/**
 * Determines whether a point within an SVG falls inside the community area of a site plan.  
 * This function is essential for handling user interactions on interactive maps, ensuring 
 * accurate detection even when transformations (such as rotations) are applied.
 * 
 * @param {HTMLElement} siteplanSvg - The SVG element representing the site plan.
 * @param {HTMLElement} pathElement - The `<path>` element outlining the community area within the SVG.
 * @param {Event} event - The mouseover event containing the X and Y coordinates of the cursor.
 * 
 * ### Important Notes for iOS:
 * - The `getScreenCTM` method does **not** propagate transformations from parent elements 
 *   to their children, as documented in MDN:
 *   https://developer.mozilla.org/en-US/docs/Web/API/SVGGraphicsElement/getScreenCTM
 * - To address this, a `<g>` (group) element is used to contain the overlay mask, ensuring 
 *   that rotations are properly interpreted.
 * - Previously, this limitation caused issues (e.g., NHS20-4527), where site plans with 
 *   rotations were non-interactive on iOS devices because this function incorrectly returned `false`.
 * 
 * ### Rotation Handling:
 * - The **rotation must remain at the overlay SVG level**, as user interactions occur on this element.
 * - However, when determining whether the interaction is within the overlay mask, 
 *   **only the `<g>` rotation should be considered**.
 * - The **SVG rotation is temporarily ignored** during this evaluation to ensure correct results.
 * 
 * @returns {boolean} `true` if the mouse is hovering over the visible portion of the site plan, otherwise `false`.
 */
const isMouseOnSitePlan = (siteplanSvg, pathElement, event) => {
    const point = siteplanSvg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;

    const parentGroup = pathElement.closest("g");
    const transformMatrix = parentGroup.getScreenCTM().inverse();
    const transformedPoint = point.matrixTransform(transformMatrix);

    return pathElement.isPointInFill(transformedPoint);
}

/**
 * getLotPathFromCoordinates will return the lot that receivid a click or tap in an Interactive Site Plan
 * @param {event} event containing information like X and Y coordinates of the click event.
 * @param {Int} sitePlanId is the site plan identifier. 
 * @returns 
 */
const getLotPathFromCoordinates = (event, sitePlanId) => {
    const sitePlan = document.querySelector(`[data-polygon-container-id="${sitePlanId}"]`);
    const svg = sitePlan?.querySelector(`svg`);
    const hitElements = document.elementsFromPoint(event.clientX, event.clientY);

    return hitElements.find((element) => {
        const idMatch = element.id?.toLowerCase()?.match(/^g.*_l.*$/);
        return idMatch && svg?.contains(element);
    });
};

/**
 * getInteractiveMap function returns information related with the interactive svg associated with an event the
 * user is triggering, like clicking, mousemove, dbclick.
 * @param {Event} event - The mouse event containing the X and Y coordinates of the current mouse cursor position.
 * @returns an interaction object containing a flag indicating whether the interaction was in the outlined area of the
 * site plan, and the actual site plan that received the interaction.
 */
const getInteractiveMap = (event) => {
    const mapOverlays = document.querySelectorAll("[data-community-map-interactive] svg");
    const candidate = {
        isMouseMovingOnMap: false,
        interactiveMap: null,
        interestedLotPath: null,
    };

    [...mapOverlays].some((mapOverlay) => {
        const originalTransform = mapOverlay.style.transform;
        mapOverlay.style.removeProperty("transform");

        [...mapOverlay.querySelectorAll("path")].some((pathElement) => {
            if (!isMouseOnSitePlan(mapOverlay, pathElement, event)) { return false; };

            candidate.isMouseMovingOnMap = true;
            candidate.interactiveMap = mapOverlay;
            candidate.communityId = +mapOverlay.parentElement.dataset.communityId;
            candidate.sitePlanId = +mapOverlay.parentElement.dataset.sitePlanId;
            candidate.isInteractive = mapOverlay.parentElement.dataset.isInteractiveSitePlan === "true";

            if (candidate.isInteractive && !candidate.interestedLotPath) {
                candidate.interestedLotPath = getLotPathFromCoordinates(event, candidate.sitePlanId);
            }

            return !!candidate.interestedLotPath;
        });

        mapOverlay.style.transform = originalTransform;

        return !!candidate.interestedLotPath;
    });

    return candidate;
};

/**
 * isUserInteractingWithSitePlan function will tell any caller control if the event
 * triggered was on a site plan outlined section.
 * @param {Event} event - The mouse event containing the X and Y coordinates of the current mouse cursor position.
 * @returns a flag indicating whether the event interaction was on a site plan.
 */
export const isUserInteractingWithSitePlan = (event) => {
    const { isMouseMovingOnMap } = getInteractiveMap(event);
    return isMouseMovingOnMap;
}

/**
 * hideSitePlansTooltips hides all of the site plan tooltips
 * and return them to their original position in case they were hovered causing
 * its coordinates to change after their original positioning.
 */
export const hideSitePlansTooltips = (sitePlanId) => {
    const tooltipsToHide = Array.from(document.querySelectorAll(`[data-site-plan-tooltip-id]`))
        .filter(tooltip => +tooltip.dataset.sitePlanTooltipId !== sitePlanId);

    tooltipsToHide.forEach(tooltip => {
        tooltip.setAttribute('hidden', '');
        tooltip.parentElement.classList.add("site-plan-tooltip_container");
    });
}

/**
 * removeActiveSitePlans function will remove active class from site plans.
 */
export const removeActiveSitePlans = (id) => {
    const activeMaps = Array.from(document.querySelectorAll(`[data-community-map-wrapper-id]`))
        .filter(activeMap => +activeMap.dataset.communityMapWrapperId !== id);

    activeMaps.forEach(activeMap => {
        activeMap.classList.remove('site-plan_image-active');
    });
}

/**
 * onMouseLeaveSitePlan will remove any active tooltip or style
 * on site plans rendered.
 */
const onMouseLeaveSitePlan = (event) => {
    const { isMouseMovingOnMap } = getInteractiveMap(event);
    if (!isMouseMovingOnMap) {
        hideSitePlansTooltips();
        removeActiveSitePlans();
    }
}

/**
 * onMouseHoveringSitePlan function is in charge of mouse moving on top of site plans.
 * @param {Object} mouseMoveInformation contains the metadata needed to handle the mouse move event.
 */
const onMouseHoveringSitePlan = (mouseMoveInformation) => {
    const {
        tooltip,
        isInInteractiveState,
        interestedLotPath,
        sitePlanInformation,
        communityId,
    } = mouseMoveInformation;

    if (!isInInteractiveState && tooltip) {
        tooltip.removeAttribute('hidden');
        const mapContainer = document.querySelector(`[data-community-map-wrapper-id="${communityId}"]:not(.site-plan_image-active)`);
        mapContainer?.classList.add('site-plan_image-active');

        return;
    }

    if (sitePlanInformation.isInteractiveSitePlan) {
        document.body.dispatchEvent(
            new CustomEvent('hoveredLot', {
                detail: {
                    interestedLotPath,
                    sitePlanId: sitePlanInformation.sitePlanId,
                    polygonId: interestedLotPath?.id
                },
            })
        );
    }
}

/**
 * onMouseMoveSitePlan function will find all of the needed information for toggling the site plan
 * tooltip.
 * @param {Event} event is the mousemove event dispatched by the user's mouse.
 */
const onMouseMoveSitePlan = (event) => {
    /**
     * If the user is clicking while mousing move, it means she/he is dragging the map around, not hovering it.
     * For these scenarios we are not displaying the tooptip as it is meant just for hovering.
     */
    if (event.buttons > 0) {
        return;
    }

    const { isMouseMovingOnMap, communityId, sitePlanId, name, interestedLotPath } = getInteractiveMap(event);
    const { isInInteractiveState, sitePlanInformation } = getSitePlan({ sitePlanId });

    const tooltip =
        document.querySelector(`[data-site-plan-tooltip-id="${sitePlanId}"]`) ||
        document.querySelector(`[data-community-map-tooltip-id="${communityId}"]`);

    const mouseMoveInformation = {
        tooltip,
        isInInteractiveState,
        sitePlanInformation,
        isMouseMovingOnMap,
        communityId,
        sitePlanId,
        event,
        interestedLotPath,
    };

    if (!isMouseMovingOnMap) {
        document.body.dispatchEvent(
            new CustomEvent('hoveredLot', {
                detail: { interestedLotPath: null, sitePlanId: null, polygonId: null },
            })
        );
    } else {
        onMouseHoveringSitePlan(mouseMoveInformation);
    }

    hideSitePlansTooltips(isMouseMovingOnMap ? sitePlanId : null);
    removeActiveSitePlans(isMouseMovingOnMap ? communityId : null);

    const communityMap = {
        id: communityId,
        name,
    }

    toggleDoubleClickZoom(isMouseMovingOnMap);

    document.body.dispatchEvent(
        new CustomEvent('communityMapMouseOver', {
          detail: {
            communityMap,
            isMouseMovingOnMap,
          },
        })
    );
};

/**
 * openUrl function open a marker url.
 * @param {Object} marker from store with the information to open an url.
 */
const openUrl = (marker) => {
    window.open(getDetailUrl(marker), '_blank');
};

/**
 * onClickCommunityInteractiveMap: will handle the logic needed after
 * clicking on a community interactive map.
 * @param {Object} sitePlanInformation from the store.
 */
const onClickCommunityInteractiveMap = (sitePlan, event, latLng) => {
    const { sitePlanInformation, isInInteractiveState } = sitePlan;
    const activeMarker = getActiveMarker(sitePlanInformation.communityId);
    const communityMap = {
        id: activeMarker?.communityId,
        name: activeMarker?.title,
        builderId: activeMarker?.builderId,
        brandName: activeMarker?.brandName,
        isInteractive: true,
    }

    if (isInInteractiveState) {
        const lotPath = getLotPathFromCoordinates(event, sitePlanInformation.sitePlanId);

        document.body.dispatchEvent(
            new CustomEvent('lotClicked', {
                detail: {
                    lotPath,
                    communityId: sitePlanInformation.communityId,
                    communityMap,
                },
            })
        );

        return;
    }

    updateMapCenter({ latitude: latLng.lat(), longitude: latLng.lng() });
    setZoomLevel(zoomLevelTriggers.zoomLevelEnterInteractiveStateTrigger);
}

/**
 * onClickCommunityStaticMap: will handle the logic needed after
 * clicking on a site plan.
 * @param {int} communityId identifier of the site plan.
 */
const onClickCommunityStaticMap = (communityId) => {
    const activeMarker = getActiveMarker(communityId);
    openUrl(activeMarker);

    const tooltip = document.querySelector(`[data-community-map-tooltip-id="${communityId}"]`);
    tooltip?.setAttribute('hidden', '');

    setActiveMarker(activeMarker);

    const communityMap = {
        id: activeMarker.communityId,
        name: activeMarker.title,
        builderId: activeMarker.builderId,
        brandName: activeMarker.brandName,
        isInteractive: false,
    }

    document.body.dispatchEvent(
        new CustomEvent('communityMapCLicked', {
            detail: { communityMap },
        })
    );
}

/**
 * onClickSitePlan: will execute the callback associated with the community clicked.
 * @param {Event} event is the click event dispatched.
 */
const onClickSitePlan = (data) => {
    const [[event], { detail: { latLng } }] = data;

    const { isMouseMovingOnMap, communityId, sitePlanId } = getInteractiveMap(event);

    if (!isMouseMovingOnMap) {
        return;
    }

    const sitePlan = getSitePlan({ sitePlanId });

    if (sitePlan.sitePlanInformation.isInteractiveSitePlan) {
        onClickCommunityInteractiveMap(sitePlan, event, latLng);
        return;
    }

    onClickCommunityStaticMap(communityId);
}

/**
 * getSitePlanWrapper function will try to find a main wrapper for the N site plans of a community.
 * If it exists, the function will return it, otherwise it will create and append one to the map container before
 * returning it.
 * @param {int} communityId is the identifier of the wrapper container.
 * @returns a DOM element representing the main container of the N site plans for a given community id.
 */
const getSitePlanWrapper = ({ communityId, isInteractiveSitePlan }) => {
    const mapWrapper = document.querySelector(`[data-community-map-wrapper-id="${communityId}"]`);
    if (mapWrapper) {
        return mapWrapper;
    }

    const newMapWrapper = document.createElement('div');
    newMapWrapper.classList.add('site-plan-wrapper_container');
    newMapWrapper.dataset.communityMapWrapperId = communityId;
    newMapWrapper.dataset.isInteractiveCommunity = isInteractiveSitePlan;
    newMapWrapper.classList.add(isInteractiveSitePlan ? "interactive" : "static");

    const polygonsContainer = document.createElement('div');
    polygonsContainer.dataset.polygonsContainerCommunityId = communityId;

    newMapWrapper.appendChild(polygonsContainer);
    map.appendChild(newMapWrapper);

    return newMapWrapper;
}

/**
 * appendSitePlanContainerToMainWrapper: will add the site plan container in sorted way inside
 * the main site plan wrapper. The sorting is important as overlapping could happen in the final
 * output and pieces of the site plan can become invisible due to other posicioned on top of them.
 */
const appendSitePlanContainerToMainWrapper = (sitePlanContainer, mainSitePlanWrapper, sitePlanConfiguration) => {
    const { rotationDegrees, sitePlanId, communityId } = sitePlanConfiguration;
    const polygonsSvgNode = sitePlanContainer.querySelector('#lot_polygons')?.closest('svg');
    const mainPolygonsContainer = document.querySelector(`[data-polygons-container-community-id="${communityId}"]`);

    const polygonsContainer = document.createElement('div');
    polygonsContainer.className = 'site-plan-polygons_container';
    polygonsContainer.style.transform = `rotate(${rotationDegrees}deg)`;
    polygonsContainer.dataset.polygonContainerId = sitePlanId;

    polygonsContainer.appendChild(polygonsSvgNode);
    mainPolygonsContainer.appendChild(polygonsContainer);
    mainSitePlanWrapper.appendChild(sitePlanContainer);
};

/**
 * getCommunityStaticMapContainer function will create the HTML container for the site plan.
 * @param {Object} sitePlanConfiguration contains information like rotation degrees for the site plan,
 * its hight and width, the url to fetch with the actual image of the site plan. among others.
 * @returns the HTML container for the site plan.
 */
export const getCommunityStaticMapContainer = async (sitePlanConfiguration) => {
    const base64image = await getEncodedImage(sitePlanConfiguration.imageUrl);

    if (!base64image) {
        return null;
    }

    const sitePlanWrapper = getSitePlanWrapper(sitePlanConfiguration);

    const svgContent = getSvgContent(sitePlanConfiguration, base64image);
    const svgUrl = `data:image/svg+xml;charset=UTF-8;base64,${(window.btoa(window.unescape(encodeURIComponent(svgContent))))}`;

    const container = document.createElement('div');
    container.classList.add('site-plan_container');
    container.dataset.communityMapId = sitePlanConfiguration.communityId;
    container.dataset.sitePlanId = sitePlanConfiguration.sitePlanId;
    container.dataset.url = sitePlanConfiguration.imageUrl;
    
    const imgElement = document.createElement("img");
    imgElement.classList.add('site-plan_image');
    imgElement.style.transform = `rotate(${sitePlanConfiguration.rotationDegrees}deg)`;
    imgElement.src = svgUrl;
    container.appendChild(imgElement);
    sitePlanWrapper.appendChild(container);

    return container;
}

/**
 * parseSVG function will take an SVG representing an interactive site plan and
 * prepare it to work in sync with elements like the mask overlay.
 * @param {string} svgContent represents the content of the SVG.
 * @param {Object} config represents the site plan configuration.
 * @returns an object containing the created SVG document and SVG element.
 */
const parseSVG = (svgContent, communityId) => {
    const parser = new DOMParser();
    const svgDocument = parser.parseFromString(svgContent, 'image/svg+xml');
    const svgElement = svgDocument.documentElement;

    const clipPathElement = svgElement.getElementById('mask');
    if (clipPathElement) {
        clipPathElement.id = communityId;
    }

    const graphicElement = svgElement.querySelector('#siteplan_graphic');
    graphicElement?.setAttribute('clip-path', `url(#${communityId})`);
    svgElement.classList.add('site-plan_image');

    return {
        svgElement,
        svgDocument,
    };
};

/**
 * createSVGGroup function will create SVG groups that will hold the interactive markers
 * representing lots for plans and specs within that community.
 * @param {HTMLElement} svgDocument representing the site plan to show.
 * @param {LotPolygonType} lotType is the lot type from LotPolygonType enum.
 * @returns the created group node.
 */
const createSVGGroup = (svgDocument, lotType) => {
    const group = svgDocument.createElementNS('http://www.w3.org/2000/svg', 'g');
    group.setAttribute('id', lotType.toString());
    return group;
};

/**
 * createLotPolygonGroups function will distribute the lot polygons within the 
 * 4 groups listed in LotPolygonType enum.
 * @param {HTMLElement} svgDocument representing the site plan to show.
 * @returns an object with the 4 groups created for the lot polygons.
 */
const createLotPolygonGroups = (svgDocument) => ({
    inactiveGroup: createSVGGroup(svgDocument, LotPolygonType.Inactive),
    activeGroup: createSVGGroup(svgDocument, LotPolygonType.Active),
    selectedGroup: createSVGGroup(svgDocument, LotPolygonType.Selected),
    hoveredGroup: createSVGGroup(svgDocument, LotPolygonType.Hovered),
});

/**
 * moveElementsToGroup function will take all of the elements in a given group node
 * and move them to the specified target group.
 * @param {HTMLElement} sourceGroup represents the source <g> group.
 * @param {HTMLElement} targetGroup represents the target <g> group.
 */
const moveElementsToGroup = (sourceGroup, targetGroup) => {
    while (sourceGroup.firstChild) {
        targetGroup.appendChild(sourceGroup.firstChild);
    }
};

/**
 * setAttributes function will take a HTML element and set the specified
 * attributes to it.
 * @param {HTMLElement} element to set the list of attributes.
 * @param {Array} attributes to set on the specified element.
 */
const setAttributes = (element, attributes) => {
    Object.entries(attributes).forEach(([key, value]) => {
        element?.setAttribute(key, value);
    });
};

/**
 * setGroupAttributes function will set a set of attributes to groups
 * of the SVG representing the Community Interactive Map.
 * @param {HTMLElement} inactiveGroup is the <g> holding inactive markers 
 * @param {HTMLElement} activeGroup is the <g> holding active markers 
 * @param {HTMLElement} selectedGroup is the <g> holding selected markers 
 * @param {HTMLElement} hoveredGroup is the <g> holding hovered markers 
 */
const setGroupAttributes = ({ inactiveGroup, activeGroup, selectedGroup, hoveredGroup }) => {
    const commonAttributes = {
        'stroke-width': '.5',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
    };

    [activeGroup, selectedGroup, hoveredGroup, inactiveGroup].forEach((group) =>
        setAttributes(group, commonAttributes)
    );

    inactiveGroup.classList.add('non-interactive-state');
};

/**
 * appendGroups function will take an array of groups and append it to a specified parent group.
 * @param {HTMLElement} parentGroup is the destination for the specified group nodes.
 * @param {Array} groups is an array of groups to be append to a given parent element.
 */
const appendGroups = (parentGroup, groups) => {
    Object.values(groups).forEach((group) => parentGroup.appendChild(group));
};

/**
 * applyStreetStyles will apply styles to the Streets layer.
 */
const applyStreetStyles = (svgElement) => {
    const fill = '#FFFFFF';
    const paths = svgElement.querySelectorAll('path');
    paths.forEach((path) => path.setAttribute('fill', fill));

    return svgElement;
}

/**
 * applyWalkwayStyles will apply styles to the Walkway layer.
 */
const applyWalkwayStyles = (svgElement) => {
    const fill = '#EBEBEB';
    const stroke = '#CDD6CA';
    const paths = svgElement.querySelectorAll('path');
    paths.forEach((path) => {
        path.setAttribute('fill', fill);
        path.setAttribute('stroke', stroke);
    });

    return svgElement;
}

/**
 * applyGrassStyles will apply styles to the Grass layer.
 */
const applyGrassStyles = (svgElement) => {
    const fill = '#B8EAC3';
    const paths = svgElement.querySelectorAll('path');
    paths.forEach((path) => path.setAttribute('fill', fill));

    return svgElement;
}

/**
 * applyGolfStyles will apply styles to the Golf layer.
 */
const applyGolfStyles = (svgElement) => {
    const paths = [...svgElement.querySelectorAll('path')];
    const [firstPath, ...otherPaths] = paths;
  
    if (firstPath) {
      firstPath.setAttribute('fill', '#90DFA2');
    }
  
    otherPaths.forEach((path) => {
      path.setAttribute('fill', '#B8EAC3');
    });

    return svgElement;
}

/**
 * applyWaterStyles will apply styles to the Water layer.
 */
const applyWaterStyles = (svgElement) => {
    const fill = '#C8DBF9';
    const paths = svgElement.querySelectorAll('path');
    paths.forEach((path) => path.setAttribute('fill', fill));

    return svgElement;
}

/**
 * applyGroundStyles will apply styles to the Ground layer.
 */
const applyGroundStyles = (svgElement) => {
    const fill = '#CCCCCC';
    const paths = svgElement.querySelectorAll('path');
    paths.forEach((path) => path.setAttribute('fill', fill));

    return svgElement;
}

/**
 * layerStylingCallbacks is a look up structure meant for applying styles to the
 * loaded layers of an Interactive Site Plan.
 */
const layerStylingCallbacks = {
    [SitePlanLayer.Street]: applyStreetStyles,
    [SitePlanLayer.Walkway]: applyWalkwayStyles,
    [SitePlanLayer.Grass]: applyGrassStyles,
    [SitePlanLayer.Golf]: applyGolfStyles,
    [SitePlanLayer.Water]: applyWaterStyles,
    [SitePlanLayer.Ground]: applyGroundStyles,
};

/**
 * getInteractiveSitePlanLayers: will lazy load layers of an interactive site plan
 * depending on the current zoom level applied in the search results map.
 * @param {int} zoomLevel of the search results map.
 * @param {Array} svgLayers collection of the layers shwon by the site plan
 * @param {HTMLElement} container of the site plan
 * @param {int} communityId of the map shown
 */
export const getInteractiveSitePlanLayers = async (zoomLevel, svgLayers, container, communityId) => {
    const loadedLayers = container.hasAttribute('data-loaded-layers')
        ? container.getAttribute('data-loaded-layers').split(',').filter(Boolean)
        : [];

    const layersToShow = Array.from(zoomLevelLayersLookup.entries())
        .filter(([key]) => key <= zoomLevel)
        .flatMap(([, layers]) => layers)
        .filter((layer) => !loadedLayers.includes(layer.name));

    const layersToFetch = svgLayers.filter(svg =>
        layersToShow.some(layer => layer.name === svg.SvgLayerType)
    );

    const fetchPromises = layersToFetch.map(layer =>
        fetch(layer.Url).then(response => (response.ok ? response.text() : null))
    );

    const svgContents = await Promise.all(fetchPromises);
    const interactiveLayers = [SitePlanLayer.LotPolygons.toString(), SitePlanLayer.Lots.toString()];

    svgContents.forEach((svgContent, index) => {
        const layer = layersToFetch[index];
        if (svgContent && layer) {
            const { svgElement, svgDocument } = parseSVG(svgContent, communityId);

            if (interactiveLayers.indexOf(layer.SvgLayerType) !== -1) {
                svgElement.style.zIndex = layer.Order;
                const lotPolygonsGroup = svgElement.querySelector('#lot_polygons');

                if (lotPolygonsGroup) {
                    const groups = createLotPolygonGroups(svgDocument);
                    moveElementsToGroup(lotPolygonsGroup, groups.inactiveGroup);
                    setGroupAttributes(groups);
                    appendGroups(lotPolygonsGroup, groups);
                }

                container.appendChild(svgElement);
            } else {
                const img = document.createElement('img');
                const styledSvg = layerStylingCallbacks[layer.SvgLayerType]?.(svgElement) || svgElement;
                img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(styledSvg.outerHTML)}`;
                img.style.zIndex = layer.Order;
                img.classList.add('site-plan_image');

                container.appendChild(img);
            }

            loadedLayers.push(layer.SvgLayerType);
            container.setAttribute('data-loaded-layers', loadedLayers.join(','));
        }
    });
};

/**
 * getCommunityInteractiveMapContainer function will create the container for an Interactive site plan.
 * @param {Object} sitePlanConfiguration holding the metadata needed for the creation of a site plan.
 * @param {int} zoomLevel is the current map zoom level. Based on it, elements will be shown or hidden.
 * @returns the HTML container for the community interactive map.
 */
export const getCommunityInteractiveMapContainer = async (sitePlanConfiguration, zoomLevel) => {
    const container = document.createElement('div');
    container.classList.add('site-plan_container');
    container.dataset.communityMapId = sitePlanConfiguration.communityId;
    container.dataset.sitePlanId = sitePlanConfiguration.sitePlanId;
    container.dataset.url = sitePlanConfiguration.imageUrl;

    await getInteractiveSitePlanLayers(
        zoomLevel,
        sitePlanConfiguration.sitePlanLayers,
        container,
        sitePlanConfiguration.communityId
    );

    const sitePlanWrapper = getSitePlanWrapper(sitePlanConfiguration);
    appendSitePlanContainerToMainWrapper(container, sitePlanWrapper, sitePlanConfiguration);
    container.style.transform = `rotate(${sitePlanConfiguration.rotationDegrees}deg)`;

    return container;
};

/**
 * getSitePlanTooltip function will create the HTML container for the site plan's tooltip.
 * @returns a HTML node representing the site plan's tooltip.
 */
export const getSitePlanTooltip = async (mapConfiguration) => {
    const { sitePlanId, communityId, isInteractiveSitePlan } = mapConfiguration;
    const tooltip = document.querySelector(
        `[data-community-map-tooltip-id="${communityId}"]`
    );

    if (tooltip && !isInteractiveSitePlan) {
        return tooltip.parentElement;
    }

    const tooltipInformation = await firstValueFrom(getCommunitySummaryCardInformation(communityId));
    const sitePlanTooltip = new PaidCommunitySummaryCard();
    sitePlanTooltip.setInformation({
        id: tooltipInformation.Id,
        name: tooltipInformation.Name,
        url: `/communitydetail/builder-${tooltipInformation.BuilderId}/community-${tooltipInformation.Id}`,
        price: tooltipInformation.Price,
        priceLow: tooltipInformation.PriceLow,
        priceHigh: tooltipInformation.PriceHigh,
        priceReduced: tooltipInformation.PriceReduced,
        thumbnail: tooltipInformation.Thumbnail,
        latitude: tooltipInformation.Latitude,
        longitude: tooltipInformation.Longitude,
        zip: tooltipInformation.Zip,
        city: tooltipInformation.City,
        stateAbbreviation: tooltipInformation.StateAbbreviation,
        builderId: tooltipInformation.BuilderId,
        brandName: tooltipInformation.BrandName,
        builderRating: tooltipInformation.BuilderRating,
        formattedBedrooms: tooltipInformation.FormattedBedrooms,
        formattedBathrooms: tooltipInformation.FormattedBathrooms,
        formattedGarages: tooltipInformation.FormattedGarages,
        formattedSquareFeet: tooltipInformation.FormattedSquareFeet,
        sitePlanCenterLatitude: mapConfiguration.isInteractiveSitePlan
            ? tooltipInformation.SitePlanCenterLatitude
            : null,
        sitePlanCenterLongitude: mapConfiguration.isInteractiveSitePlan
            ? tooltipInformation.SitePlanCenterLongitude
            : null,
        marketingStatus: tooltipInformation.MarketingStatus,
        isAdult: tooltipInformation.IsAdult,
        communityCount: tooltipInformation.TotalPlans,
    });

    const sitePlanTooltipContainer = document.createElement('div');
    sitePlanTooltipContainer.dataset.tooltipContainer = true;
    sitePlanTooltipContainer.classList.add("site-plan-tooltip_container");
    sitePlanTooltipContainer.innerHTML = sitePlanTooltip.getHtml();
    sitePlanTooltipContainer.firstElementChild.dataset.communityMapTooltipId = communityId;
    sitePlanTooltipContainer.firstElementChild.dataset.sitePlanTooltipId = sitePlanId;
    map.appendChild(sitePlanTooltipContainer);

    return sitePlanTooltipContainer;
};

/**
 * removeSitePlan function is called whenever a site plan is destroyed. It deletes any added resource
 * to the DOM related to the removed site plan.
 */
export const removeSitePlan = (resource) => {
    resource.container.parentElement?.remove();
    resource.container.remove();
    resource.overlay.remove();
    resource.tooltip?.remove();
    resource.polygonsContainer?.remove();
    resource.onSingleClick$.unsubscribe();
    resource.onMouseMove$.unsubscribe();
    resource.onMouseLeave$.unsubscribe();
}

/**
 * applySitePlanStylesOnDraw will position the HTML site plan elements in terms of the map coordinates and zoom. 
 */
const applySitePlanStylesOnDraw = (southWestPoint, northEastPoint, sitePlanContainer, sitePlanId) => {
    const polygonContainer = document.querySelector(`[data-polygon-container-id="${sitePlanId}"]`);

    [polygonContainer, sitePlanContainer].forEach((container) => {
        if (container) {
            container.style.left = `${southWestPoint.x}px`;
            container.style.top = `${northEastPoint.y}px`;
            container.style.width = `${northEastPoint.x - southWestPoint.x}px`;
            container.style.height = `${southWestPoint.y - northEastPoint.y}px`;
        }
    })
}

/**
 * handleSitePlanVisibility will show or hide the site plan containers depending on its visibility
 * relative to the viewport.
 * @param {HTMLElement} sitePlanContainer is the site plan container.
 * @param {int} sitePlanId is the site plan identifier. 
 */
const handleSitePlanVisibility = (sitePlanContainer, sitePlanId, isSitePlanVisible) => {
    const polygonContainer = document.querySelector(`[data-polygon-container-id="${sitePlanId}"]`);

    [polygonContainer, sitePlanContainer].forEach((container) => {
        if (container) {
            container.style.display = isSitePlanVisible ? 'block' : 'none';
        }
    });
}

/**
 * drawSitePlan function is called whenever a Community site plan is draw.
 * It positiones the Community site plan resources in the DOM at the desired coordinates in terms of pixels.
 */
export const drawSitePlan = (resource, southWestPoint, northEastPoint) => {
    const { overlay, container, tooltip, communityId, sitePlanId } = resource;

    const isSitePlanVisible = isSitePlanInViewport(sitePlanId);
    handleSitePlanVisibility(container, sitePlanId, isSitePlanVisible);

    if (!isSitePlanVisible) {
        return;
    }

    const overlaySvg = overlay.querySelector('svg');
    applySitePlanStylesOnDraw(southWestPoint, northEastPoint, container, sitePlanId);

    overlaySvg.style.left = `${southWestPoint.x}px`;
    overlaySvg.style.top = `${northEastPoint.y}px`;
    overlaySvg.style.width = `${northEastPoint.x - southWestPoint.x}px`;
    overlaySvg.style.height = `${southWestPoint.y - northEastPoint.y}px`;

    const centerX = southWestPoint.x + (northEastPoint.x - southWestPoint.x) / 2;
    const centerY = northEastPoint.y + (southWestPoint.y - northEastPoint.y) / 2;

    if (tooltip) {
        tooltip.firstElementChild.style.left = `${centerX}px`;
        tooltip.firstElementChild.style.top = `${centerY}px`;
        tooltip.firstElementChild.setAttribute('hidden', '');
    }

    document.body.dispatchEvent(
        new CustomEvent('communityMapDrawComplete', {
          detail: {
            communityId,
            sitePlanId,
          },
        })
      );
  }

/**
 * The `searchSitePlans` function returns an observable responsible for retrieving 
 * site plan information. It manages both successful responses and error interactions 
 * with the server, ensuring a smooth flow for the calling control.
 * 
 * @param {Array} communityIds - The list of community IDs to be fetched from the server.
 * @returns {Observable} An observable that handles the retrieval of site plans data.
 */
export const searchSitePlans$ = (markers) => {
    const payload = {
        communityIds: markers.map(marker => marker.communityId),
    };

    return searchSitePlansInformation$(payload).pipe(
        switchMap(sitePlans => of({ sitePlans, markers })),
        catchError(() => of([])),
    );
}

/**
 * Creates an observable that handles mouse click events on a site plan image.
 * @param {HTML} sitePlanImage The element that receives the clicks.
 * @param {number} expectedClickCount The expected number of clicks to consider (1 for single, >1 for double).
 * @param {function} handler The function to handle the resulting click events.
 * @returns Observable for handling clicks in the site plan.
 */
const createClickObservable = (sitePlanImage, expectedClickCount, handler) => {
    const mouseDown$ = fromEvent(sitePlanImage, 'pointerdown');
    const mouseUp$ = fromEvent(sitePlanImage, 'pointerup');

    return mouseDown$.pipe(
        switchMap((startEvent) => {
            const startX = startEvent.clientX;
            const startY = startEvent.clientY;

            return mouseUp$.pipe(
                take(1),
                filter((endEvent) => {
                    const endX = endEvent.clientX;
                    const endY = endEvent.clientY;
                    const movementThreshold = 5;

                    return (
                        Math.abs(endX - startX) < movementThreshold &&
                        Math.abs(endY - startY) < movementThreshold
                    );
                })
            );
        }),
        buffer(mouseDown$.pipe(debounceTime(250))),
        filter(clicks => clicks.length === expectedClickCount),
        withLatestFrom(mapClicked$)
    ).subscribe(handler);
};

/**
 * onDragStart will hide any shown tooltips for site plans.
 */
const onDragStart = () => {
    hideSitePlansTooltips();
}

mapDragStart$.subscribe(onDragStart);

/**
 * onSingleClick$ observable will handle single click events on a site plan.
 * @param {HTML} container that received the single click.
 * @returns observable for handling single clicks in site plan.
 */
export const onSingleClick$ = (container) => createClickObservable(container, 1, onClickSitePlan);

/**
 * onMouseMove$ observable will handle mouse move events on a site plan.
 * @param {HTML} container that received the mouse move.
 * @returns observable for handling mouse move in site plan.
 */
export const onMouseMove$ = (container) =>
    fromEvent(container, 'mousemove').subscribe(onMouseMoveSitePlan);

/**
 * onMouseLeave$ observable will handle mouse leave events on a site plan.
 * @param {HTML} container that received the mouse leave.
 * @returns observable for handling mouse leave in site plan.
 */
export const onMouseLeave$ = (container) =>
    fromEvent(container, 'mouseleave').subscribe(onMouseLeaveSitePlan);